import { rrulestr, RRule, RRuleSet } from 'rrule';
import moment from 'moment-timezone';
import { CompactCalendarEvent } from '../../components/CompactMonthCalendar/types';
import { Announcement } from '../../types/Announcement';

/**
 * Date convention, shared by both breakpoints: an announcement's facility wall-clock
 * time is projected onto a browser-local civil Date. FullCalendar runs with its default
 * `timeZone: 'local'`, so this is what makes the grid render facility time rather than
 * the viewer's. The compact agenda must use the same projection or the two views would
 * disagree about which day an event falls on.
 */
const convertToFacilityTimezone = (date: string | number | Date, facilityTimezone: string) => {
    return moment.utc(date).tz(facilityTimezone).format('YYYY-MM-DDTHH:mm:ss');
};

// Moved from announcements/index.tsx
// Facility wall-clock is formatted without Z then parsed as a local Date because
// FullCalendar default timeZone is 'local'
const getAnnouncementStart = (event: Announcement, facilityTimezone: string): Date => {
    if (event.triggerTimestampISO) {
        return new Date(convertToFacilityTimezone(event.triggerTimestampISO, facilityTimezone));
    }
    if (event.startDate) {
        // 'YYYY-MM-DD' would parse as UTC midnight, landing on the previous
        // civil day west of UTC. Give it a time so it stays a civil date, like
        // the triggerTimestampISO branch above.
        return new Date(`${event.startDate}T00:00:00`);
    }
    return new Date(convertToFacilityTimezone(event.DateAdded, facilityTimezone));
};

// end: 1) endDate (same facility wall-clock projection as start)
//      2) start (same instant → zero-duration)
const getAnnouncementEnd = (
    event: Announcement,
    startDateObj: Date,
    facilityTimezone: string,
): Date => {
    if (!event.endDate) return startDateObj;
    // Recurring endDate is series UNTIL (YYYY-MM-DD from NotificationLog), not
    // occurrence end. Using it as duration would paint every send as a multi-day
    // span. Point-in-time send → same instant as start, like missing endDate.
    if (event.Recurrence) return startDateObj;
    return new Date(convertToFacilityTimezone(event.endDate, facilityTimezone));
};

/** Exactly the shape handed to `<FullCalendar events={...} />`. */
export interface AnnouncementCalendarEvent {
    title: string;
    creatorInfo: Announcement['creatorInfo'];
    ResidentNames?: string[];
    start: Date;
    end: Date;
    id: string;
    rrule?: string;
    exdate?: string[];
}

/**
 * Splits a recurrence string into the `rrule` / `exdate` pair FullCalendar expects.
 */
export const normalizeRecurrence = (recurrence: string): { rrule: string; exdate: string[] } => {
    const lines = recurrence.split('\n');
    const second = lines[1] || '';
    if (/exdate/i.test(second)) {
        const exdate = (second.split(':')[1] || '').split(',').filter(Boolean);
        const rrule = [lines[0], ...lines.slice(2)].join('\n');
        return { rrule, exdate };
    }
    return { rrule: recurrence, exdate: [] };
};

export const mapAnnouncementsToFullCalendarEvents = (
    announcements: Announcement[],
    facilityTimezone: string,
): AnnouncementCalendarEvent[] => {
    return announcements.map((event) => {
        const startDateObj = getAnnouncementStart(event, facilityTimezone);
        const endDateObj = getAnnouncementEnd(event, startDateObj, facilityTimezone);

        const base = {
            title: event.Message,
            creatorInfo: event.creatorInfo,
            ResidentNames: event.ResidentNames,
            start: startDateObj,
            end: endDateObj,
            id: event._id,
        };

        if (!event.Recurrence) return base;

        const { rrule, exdate } = normalizeRecurrence(event.Recurrence);
        return { ...base, rrule, exdate };
    });
};

/** EXDATE tokens may be `20260812` or `20260812T000000Z`; only the day part matters. */
const exdateDayKey = (token: string) => token.substring(0, 8);

/** Day key of an occurrence. rrule keeps the civil date in UTC fields. */
const occurrenceDayKey = (occurrence: Date): string =>
    `${occurrence.getUTCFullYear()}${String(occurrence.getUTCMonth() + 1).padStart(
        2,
        '0',
    )}${String(occurrence.getUTCDate()).padStart(2, '0')}`;

/**
 * An occurrence's civil date as a browser-local Date, carrying the series' own
 * time of day. The only crossing from rrule's field space back into local time.
 */
const occurrenceWithBaseTime = (occurrence: Date, timeSource: Date): Date =>
    new Date(
        occurrence.getUTCFullYear(),
        occurrence.getUTCMonth(),
        occurrence.getUTCDate(),
        timeSource.getHours(),
        timeSource.getMinutes(),
        timeSource.getSeconds(),
        0,
    );

/**
 * Local civil fields re-hosted on a UTC Date. rrule does its calendar math with
 * getUTC* / Date.UTC, so this is the field space DTSTART:YYYYMMDD already parses
 * into, and the space the range bounds must be in to be comparable with it.
 */
const toFloatingUtc = (date: Date): Date =>
    new Date(
        Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes(),
            date.getSeconds(),
            0,
        ),
    );

const isUtcMidnight = (date: Date): boolean =>
    date.getUTCHours() === 0 &&
    date.getUTCMinutes() === 0 &&
    date.getUTCSeconds() === 0 &&
    date.getUTCMilliseconds() === 0;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Date-only UNTIL=YYYYMMDD parses to UTC midnight, which clips any occurrence
 * later than 00:00 on that last civil day. Extend it to the end of the same day
 * without leaving rrule's field space, so UNTIL keeps whatever domain DTSTART
 * has — they always come from the same recurrence string.
 */
const inclusiveUntil = (until: Date | null | undefined): Date | null | undefined => {
    if (!until || !isUtcMidnight(until)) return until;
    return new Date(until.getTime() + DAY_MS - 1);
};

const origOptionsFromParsed = (parsed: ReturnType<typeof rrulestr>) => {
    if (parsed instanceof RRuleSet) {
        const rules = parsed.rrules();
        return rules.length > 0 ? rules[0].origOptions : {};
    }
    return parsed.origOptions;
};

const toCompactEvent = (
    event: AnnouncementCalendarEvent,
    start: Date,
    end: Date,
    occurrenceKey: string,
): CompactCalendarEvent => ({
    occurrenceKey,
    id: event.id,
    title: event.title,
    start,
    end,
    timeLabel: moment(start).format('h:mm'),
    timeSuffix: moment(start).format('A'),
    // Mirrors FullCalendar's `extendedProps` so both click paths hand the modal
    // an identical payload.
    meta: {
        creatorInfo: event.creatorInfo,
        ResidentNames: event.ResidentNames,
        rrRule: event.rrule,
        exDate: event.exdate || [],
    },
});

/**
 * Expands `events` into concrete occurrences in [rangeStart, rangeEnd) for the
 * compact month grid and agenda. Recurring series are expanded with rrule
 * because FullCalendar (and @fullcalendar/rrule) is not mounted at ≤1024px.
 * `events` is the same AnnouncementCalendarEvent[] passed to
 * <FullCalendar events={...} /> — already through normalizeRecurrence.
 *
 * Expansion keeps the recurrence string's own DTSTART. `DTSTART:YYYYMMDD` parses
 * to UTC midnight, so rrule's UTC-field math already reads the intended civil
 * day — the same floating reading @fullcalendar/rrule gives it on desktop, which
 * is what keeps the two breakpoints on identical days. Occurrences stay in that
 * field space until the last step, so no UTC offset can shift the calendar day.
 */
export const expandCalendarEventsForRange = (
    events: AnnouncementCalendarEvent[],
    rangeStart: Date,
    rangeEnd: Date,
): CompactCalendarEvent[] => {
    const results: CompactCalendarEvent[] = [];

    events.forEach((event) => {
        const pushSingleOccurrence = () => {
            if (event.start < rangeEnd && event.end >= rangeStart) {
                results.push(toCompactEvent(event, event.start, event.end, event.id));
            }
        };

        if (!event.rrule) {
            pushSingleOccurrence();
            return;
        }

        try {
            const excluded = new Set((event.exdate || []).map(exdateDayKey));
            const durationMs = Math.max(0, event.end.getTime() - event.start.getTime());
            const orig = origOptionsFromParsed(rrulestr(event.rrule));
            const rule = new RRule({
                ...orig,
                // Only supply an anchor when the string has none; rrule would
                // otherwise default DTSTART to today.
                dtstart: orig.dtstart || toFloatingUtc(event.start),
                until: inclusiveUntil(orig.until),
                tzid: undefined,
            });

            // rangeEnd is exclusive (FullCalendar datesSet style).
            const floatingRangeEnd = toFloatingUtc(rangeEnd);
            rule
                .between(toFloatingUtc(rangeStart), floatingRangeEnd, true)
                .filter((occurrence) => occurrence.getTime() < floatingRangeEnd.getTime())
                .forEach((occurrence) => {
                    const dayKey = occurrenceDayKey(occurrence);
                    if (excluded.has(dayKey)) return;
                    const occStart = occurrenceWithBaseTime(occurrence, event.start);
                    const occEnd = new Date(occStart.getTime() + durationMs);
                    results.push(
                        toCompactEvent(event, occStart, occEnd, `${event.id}-${dayKey}`),
                    );
                });
        } catch (error) {
            // Surface the parse failure — falling back to the series start silently
            // would hide every other occurrence in the month.
            // eslint-disable-next-line no-console
            console.warn('Unable to expand recurrence for announcement', event.id, error);
            pushSingleOccurrence();
        }
    });

    return results.sort((a, b) => a.start.getTime() - b.start.getTime());
};
